import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {baseUrl, userName} from "./Univ";
function Home() {
    /* ============================================================
       DATE
       ============================================================ */
    const getLocalDate = () => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };
    const today = getLocalDate();
    const currentDate = new Date().toLocaleDateString("en-IN", {
        day: "2-digit", month: "long", year: "numeric"
    });
    /* ============================================================
       USER
       ============================================================ */
    const user = userName || "Farm Administrator";
    /* ============================================================
       STATES
       ============================================================ */
    const [farmlands, setFarmlands] = useState([]);
    const [landCrops, setLandCrops] = useState([]);
    const [devices, setDevices] = useState([]);
    const [gateways, setGateways] = useState([]);
    const [observations, setObservations] = useState([]);
    const [alarms, setAlarms] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(false);
    /* ============================================================
       GENERIC ARRAY HELPER
       ============================================================ */
    const getArray = (data) => {
        if (Array.isArray(data)) {
            return data;
        }
        if (Array.isArray(data?.results)) {
            return data.results;
        }
        return [];
    };
    /* ============================================================
       LOAD AGRI DATA
       ============================================================ */
    const loadDashboard = async () => {
        try {
            setLoading(true);
            const requests = [// FARMLAND
                axios.get(`${baseUrl}farmland/`), // LAND CROPS
                axios.get(`${baseUrl}land-crop/`), // IOT DEVICES
                axios.get(`${baseUrl}iot-devices/`), // IOT GATEWAYS
                axios.get(`${baseUrl}iot-gateways/`), // IOT ALARMS
                axios.get(`${baseUrl}iot-alarms/`), // DEVICE → FARMLAND MAPPING
                axios.get(`${baseUrl}iot-device-mapping/`)];
            const responses = await Promise.allSettled(requests);
            setFarmlands(responses[0].status === "fulfilled" ? getArray(responses[0].value.data) : []);
            setLandCrops(responses[1].status === "fulfilled" ? getArray(responses[1].value.data) : []);
            setDevices(responses[2].status === "fulfilled" ? getArray(responses[2].value.data) : []);
            setGateways(responses[3].status === "fulfilled" ? getArray(responses[3].value.data) : []);
            setAlarms(responses[4].status === "fulfilled" ? getArray(responses[4].value.data) : []);
            setMappings(responses[5].status === "fulfilled" ? getArray(responses[5].value.data) : []);
        } catch (error) {
            console.log("Agri Dashboard Error:", error);
        } finally {
            setLoading(false);
        }
    };
    /* ============================================================
       INITIAL LOAD + LIVE REFRESH
       ============================================================ */
    useEffect(() => {
        loadDashboard();
        const interval = setInterval(loadDashboard, 30000);
        return () => clearInterval(interval);
    }, []);
    /* ============================================================
       BASIC COUNTS
       ============================================================ */
    const totalFarmlands = farmlands.length;
    const totalCrops = landCrops.length;
    const totalDevices = devices.length;
    const totalGateways = gateways.length;
    const onlineDevices = devices.filter(device => device.is_online === true || device.status === "ONLINE").length;
    const offlineDevices = Math.max(0, totalDevices - onlineDevices);
    const activeMappings = mappings.filter(mapping => mapping.active === true).length;
    const openAlarms = alarms.filter(alarm => alarm.status === "OPEN");
    const criticalAlarms = openAlarms.filter(alarm => alarm.severity === "CRITICAL");
    const warningAlarms = openAlarms.filter(alarm => alarm.severity === "WARNING");
    /* ============================================================
       DEVICE TYPE DISTRIBUTION
       ============================================================ */
    const deviceTypeData = useMemo(() => {
        const map = {};
        devices.forEach(device => {
            const type = device.device_type || "OTHER";
            map[type] = (map[type] || 0) + 1;
        });
        return Object.entries(map)
            .map(([name, value]) => ({
                name, value
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 8);
    }, [devices]);
    /* ============================================================
       CROP DISTRIBUTION
       ============================================================ */
    const cropDistribution = useMemo(() => {
        const map = {};
        landCrops.forEach(crop => {
            const name = crop.crop_name || crop.crop || crop.name || crop.crop__name || "Unknown Crop";
            map[name] = (map[name] || 0) + 1;
        });
        return Object.entries(map)
            .map(([name, value]) => ({
                name, value
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 8);
    }, [landCrops]);
    /* ============================================================
       OBSERVATION HELPER
       ============================================================ */
    const parameterValue = (parameter) => {
        const matching = observations
            .filter(observation => String(observation.parameter || "").toUpperCase() === parameter)
            .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at));
        if (matching.length === 0) {
            return null;
        }
        return matching[0];
    };
    /* ============================================================
       LIVE SENSOR VALUES
       ============================================================ */
    const soilMoisture = parameterValue("SOIL_MOISTURE");
    const soilTemperature = parameterValue("SOIL_TEMPERATURE");
    const soilPh = parameterValue("SOIL_PH");
    const soilEc = parameterValue("SOIL_EC");
    const rainfall = parameterValue("RAINFALL");
    const humidity = parameterValue("HUMIDITY");
    const temperature = parameterValue("TEMPERATURE");
    const windSpeed = parameterValue("WIND_SPEED");
    /* ============================================================
       LIVE SENSOR DISPLAY
       ============================================================ */
    const sensorValue = (observation) => {
        if (!observation) {
            return "--";
        }
        return observation.value ?? "--";
    };
    const sensorUnit = (observation) => {
        if (!observation) {
            return "";
        }
        return observation.unit || "";
    };
    /* ============================================================
       OBSERVATION TREND
       ============================================================ */
    const observationTrend = useMemo(() => {
        const result = [];
        for (let i = 6; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            const dateString = date.toISOString()
                .substring(0, 10);
            const count = observations.filter(observation => String(observation.recorded_at || "").substring(0, 10) === dateString).length;
            result.push({
                date: date.toLocaleDateString("en-IN", {
                    day: "2-digit", month: "short"
                }), observations: count
            });
        }
        return result;
    }, [observations]);
    /* ============================================================
       ALARM DISTRIBUTION
       ============================================================ */
    const alarmDistribution = [{
        name: "Critical", value: criticalAlarms.length
    }, {
        name: "Warning", value: warningAlarms.length
    }, {
        name: "Info", value: openAlarms.filter(alarm => alarm.severity === "INFO").length
    }];
    /* ============================================================
       DEVICE STATUS
       ============================================================ */
    const deviceStatusData = [{
        name: "Online", value: onlineDevices
    }, {
        name: "Offline", value: offlineDevices
    }];
    /* ============================================================
       LAST OBSERVATIONS
       ============================================================ */
    const latestObservations = useMemo(() => {
        return [soilMoisture, soilTemperature, soilPh, soilEc, rainfall, humidity, temperature, windSpeed].filter(Boolean);
    }, [soilMoisture, soilTemperature, soilPh, soilEc, rainfall, humidity, temperature, windSpeed]);
    /* ============================================================
       MONEY / NUMBER FORMAT
       ============================================================ */
    const number = value => Number(value || 0).toLocaleString("en-IN");
    /* ============================================================
       RENDER
       ============================================================ */
    return (<div className="drhis-home">


        {/* ====================================================
               DASHBOARD HEADER
               ==================================================== */}

        <div className="dashboard-header">

            <div>

                <div className="dashboard-brand">

                    DrGHiS • AGRICULTURE
                    INTELLIGENCE

                </div>


                <h4 className="dashboard-title">

                    Farm Intelligence
                    Dashboard

                </h4>

            </div>


            <div className="dashboard-header-right">

                <div className="dashboard-date">

                    {currentDate}

                </div>


                <div className="dashboard-live-text">

                    Real-time agriculture
                    monitoring

                </div>

            </div>

        </div>


        {/* ====================================================
               CONTEXT BAR
               ==================================================== */}

        <div className="dashboard-context">

            <div className="row align-items-center">

                <div className="col-md-3">

                    <div className="context-label">

                        REPORTING PERIOD

                    </div>

                    <div className="context-value">

                        Today

                    </div>

                </div>


                <div className="col-md-3">

                    <div className="context-label">

                        FARM STATUS

                    </div>


                    <div className="system-status">

                        <span className="status-dot"/>

                        Agriculture System
                        Operational

                    </div>

                </div>


                <div className="col-md-3">

                    <div className="context-label">

                        USER

                    </div>


                    <div className="context-value">

                        {user}

                    </div>

                </div>


                <div className="col-md-3 text-end">

                        <span className="live-badge">

                            AGRI • LIVE

                        </span>

                </div>

            </div>

        </div>


        {/* ====================================================
               FARM KPIs
               ==================================================== */}

        <SectionTitle
            title="Farm & Crop KPIs"
        />


        <div className="row g-3">


            <DashboardCard
                title="Farmlands"
                value={number(totalFarmlands)}
                subtitle="Registered farms"
                icon="FM"
                accent="green"
            />


            <DashboardCard
                title="Active Crops"
                value={number(totalCrops)}
                subtitle="Land crop records"
                icon="CR"
                accent="blue"
            />


            <DashboardCard
                title="Farm Mappings"
                value={number(activeMappings)}
                subtitle="Active IoT mappings"
                icon="MP"
                accent="cyan"
            />


            <DashboardCard
                title="IoT Gateways"
                value={number(totalGateways)}
                subtitle="Registered gateways"
                icon="GW"
                accent="purple"
            />


            <DashboardCard
                title="IoT Devices"
                value={number(totalDevices)}
                subtitle="Registered devices"
                icon="DV"
                accent="orange"
            />


            <DashboardCard
                title="Online Devices"
                value={number(onlineDevices)}
                subtitle="Currently connected"
                icon="ON"
                accent="green"
            />


            <DashboardCard
                title="Offline Devices"
                value={number(offlineDevices)}
                subtitle="Requires attention"
                icon="OF"
                accent="red"
            />


            <DashboardCard
                title="Open Alarms"
                value={number(openAlarms.length)}
                subtitle="Active alerts"
                icon="AL"
                accent="red"
            />

        </div>


        {/* ====================================================
               ENVIRONMENTAL KPIs
               ==================================================== */}

        <SectionTitle
            title="Live Farm Environment"
        />


        <div className="row g-3">


            <SensorCard
                title="Soil Moisture"
                value={sensorValue(soilMoisture)}
                unit={sensorUnit(soilMoisture) || "%"}
                subtitle="Current field moisture"
                icon="💧"
                accent="blue"
            />


            <SensorCard
                title="Soil Temperature"
                value={sensorValue(soilTemperature)}
                unit={sensorUnit(soilTemperature) || "°C"}
                subtitle="Root zone temperature"
                icon="🌡"
                accent="orange"
            />


            <SensorCard
                title="Soil pH"
                value={sensorValue(soilPh)}
                unit=""
                subtitle="Current soil acidity"
                icon="🧪"
                accent="purple"
            />


            <SensorCard
                title="Soil EC"
                value={sensorValue(soilEc)}
                unit={sensorUnit(soilEc)}
                subtitle="Electrical conductivity"
                icon="⚡"
                accent="cyan"
            />


            <SensorCard
                title="Rainfall"
                value={sensorValue(rainfall)}
                unit={sensorUnit(rainfall) || "mm"}
                subtitle="Latest rainfall reading"
                icon="🌧"
                accent="blue"
            />


            <SensorCard
                title="Humidity"
                value={sensorValue(humidity)}
                unit={sensorUnit(humidity) || "%"}
                subtitle="Ambient humidity"
                icon="💨"
                accent="cyan"
            />

        </div>


        {/* ====================================================
               AGRICULTURE ANALYTICS
               ==================================================== */}

        <SectionTitle
            title="Agriculture Intelligence Analytics"
            className="dashboard-section-spacing"
        />


        <div className="row g-3">


            {/* OBSERVATION TREND */}

            <div className="col-lg-8">

                <EnterpriseCard
                    title="IoT Observation Activity"
                    subtitle="Farm sensor activity • Last 7 days"
                >

                    <div className="chart-container chart-large">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <LineChart
                                data={observationTrend}
                            >

                                <CartesianGrid
                                    className="chart-grid"
                                    strokeDasharray="3 3"
                                />

                                <XAxis
                                    dataKey="date"
                                    className="chart-axis"
                                />

                                <YAxis
                                    allowDecimals={false}
                                    className="chart-axis"
                                />

                                <Tooltip/>


                                <Line
                                    className="chart-line-green"
                                    type="monotone"
                                    dataKey="observations"
                                    name="Observations"
                                    strokeWidth={3}
                                    dot={{
                                        r: 4
                                    }}
                                    activeDot={{
                                        r: 6
                                    }}
                                />

                            </LineChart>

                        </ResponsiveContainer>

                    </div>

                </EnterpriseCard>

            </div>


            {/* DEVICE STATUS */}

            <div className="col-lg-4">

                <EnterpriseCard
                    title="IoT Device Health"
                    subtitle="Current device connectivity"
                >

                    <div className="chart-container chart-large">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <PieChart>

                                <Pie
                                    data={deviceStatusData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="45%"
                                    outerRadius={90}
                                    innerRadius={45}
                                    label
                                >

                                    <Cell
                                        className="pie-green"
                                    />

                                    <Cell
                                        className="pie-red"
                                    />

                                </Pie>

                                <Tooltip/>

                                <Legend/>

                            </PieChart>

                        </ResponsiveContainer>

                    </div>

                </EnterpriseCard>

            </div>

        </div>


        {/* ====================================================
               SECOND ANALYTICS ROW
               ==================================================== */}

        <div className="row g-3 dashboard-row-spacing">


            {/* CROP DISTRIBUTION */}

            <div className="col-lg-4">

                <EnterpriseCard
                    title="Crop Distribution"
                    subtitle="Current land crop portfolio"
                >

                    <div className="chart-container chart-medium">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <BarChart
                                data={cropDistribution}
                                layout="vertical"
                                margin={{
                                    left: 20, right: 20
                                }}
                            >

                                <CartesianGrid
                                    className="chart-grid"
                                    strokeDasharray="3 3"
                                />

                                <XAxis
                                    type="number"
                                    allowDecimals={false}
                                    className="chart-axis"
                                />

                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    className="chart-axis"
                                    width={90}
                                />

                                <Tooltip/>

                                <Bar
                                    className="chart-bar-green"
                                    dataKey="value"
                                    name="Crops"
                                    radius={[0, 4, 4, 0]}
                                />

                            </BarChart>

                        </ResponsiveContainer>

                    </div>

                </EnterpriseCard>

            </div>


            {/* DEVICE TYPES */}

            <div className="col-lg-4">

                <EnterpriseCard
                    title="IoT Device Distribution"
                    subtitle="Sensors and farm equipment"
                >

                    <div className="chart-container chart-medium">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <PieChart>

                                <Pie
                                    data={deviceTypeData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="45%"
                                    outerRadius={85}
                                    innerRadius={40}
                                    label
                                >

                                    {deviceTypeData.map((item, index) => (<Cell
                                        key={index}
                                        className={`pie-${(index % 4)}`}
                                    />))}

                                </Pie>

                                <Tooltip/>

                                <Legend/>

                            </PieChart>

                        </ResponsiveContainer>

                    </div>

                </EnterpriseCard>

            </div>


            {/* ALARMS */}

            <div className="col-lg-4">

                <EnterpriseCard
                    title="Farm Alerts"
                    subtitle="Active IoT intelligence alerts"
                >

                    <div className="chart-container chart-medium">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <PieChart>

                                <Pie
                                    data={alarmDistribution}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="45%"
                                    outerRadius={85}
                                    innerRadius={40}
                                    label
                                >

                                    <Cell
                                        className="pie-red"
                                    />

                                    <Cell
                                        className="pie-orange"
                                    />

                                    <Cell
                                        className="pie-blue"
                                    />

                                </Pie>

                                <Tooltip/>

                                <Legend/>

                            </PieChart>

                        </ResponsiveContainer>

                    </div>

                </EnterpriseCard>

            </div>

        </div>


        {/* ====================================================
               LIVE SENSOR WORKSPACE
               ==================================================== */}

        <SectionTitle
            title="Live Farm Sensor Workspace"
            className="dashboard-section-spacing"
        />


        <div className="row g-3">


            {/* LATEST OBSERVATIONS */}

            <div className="col-lg-8">

                <EnterpriseCard
                    title="Latest Sensor Observations"
                    subtitle="Most recent field readings"
                >

                    <div className="table-responsive">

                        <table className="table-erp-modern">

                            <thead>

                            <tr>

                                <th>
                                    Parameter
                                </th>

                                <th>
                                    Value
                                </th>

                                <th>
                                    Unit
                                </th>

                                <th>
                                    Status
                                </th>

                                <th>
                                    Recorded
                                </th>

                            </tr>

                            </thead>


                            <tbody>

                            {latestObservations.length === 0 ? (<tr>

                                <td
                                    colSpan="5"
                                    className="text-center text-muted"
                                >

                                    No live sensor observations

                                </td>

                            </tr>) : (latestObservations.map(observation => (<tr
                                key={observation.id}
                            >

                                <td>

                                    <strong>
                                        {observation.parameter}
                                    </strong>

                                </td>

                                <td>

                                    {observation.value ?? "--"}

                                </td>

                                <td>

                                    {observation.unit || "--"}

                                </td>

                                <td>

                                                        <span
                                                            className={observation.status === "NORMAL" ? "iot-status online" : "iot-status offline"}
                                                        >

                                                            {observation.status || "RECEIVED"}

                                                        </span>

                                </td>

                                <td>

                                    {observation.recorded_at}

                                </td>

                            </tr>)))}

                            </tbody>

                        </table>

                    </div>

                </EnterpriseCard>

            </div>


            {/* ACTIVE ALERTS */}

            <div className="col-lg-4">

                <EnterpriseCard
                    title="Active Farm Alerts"
                    subtitle="Immediate attention required"
                >

                    <div className="agri-alert-list">

                        {openAlarms.length === 0 ? (<EmptyState
                            text="No active farm alerts"
                        />) : (openAlarms
                            .slice(0, 6)
                            .map(alarm => (<div
                                key={alarm.id}
                                className={`agri-alert-item ${alarm.severity === "CRITICAL" ? "alert-critical" : alarm.severity === "WARNING" ? "alert-warning" : "alert-info"}`}
                            >

                                <div>

                                    <strong>

                                        {alarm.parameter}

                                    </strong>


                                    <div className="small">

                                        {alarm.message}

                                    </div>

                                </div>


                                <span className="alert-value">

                                                    {alarm.value}

                                                </span>

                            </div>)))}

                    </div>

                </EnterpriseCard>

            </div>

        </div>


        {/* ====================================================
               AGRICULTURE CONTROL CENTER
               ==================================================== */}

        <SectionTitle
            title="Agriculture Control Center"
            className="dashboard-section-spacing"
        />


        <div className="row g-3">


            <div className="col-lg-3">

                <ControlCard
                    icon="🌾"
                    title="Farmland"
                    value={number(totalFarmlands)}
                    subtitle="Registered agricultural land"
                />

            </div>


            <div className="col-lg-3">

                <ControlCard
                    icon="🌱"
                    title="Crop Portfolio"
                    value={number(totalCrops)}
                    subtitle="Active crop records"
                />

            </div>


            <div className="col-lg-3">

                <ControlCard
                    icon="📡"
                    title="Connected IoT"
                    value={`${number(onlineDevices)} / ${number(totalDevices)}`}
                    subtitle="Online device status"
                />

            </div>


            <div className="col-lg-3">

                <ControlCard
                    icon="🚨"
                    title="Critical Alerts"
                    value={number(criticalAlarms.length)}
                    subtitle="Requires immediate action"
                    danger
                />

            </div>

        </div>


        {/* ====================================================
               FOOTER
               ==================================================== */}

        <div className="dashboard-footer">

            DrGHiS Agriculture Intelligence System

            &nbsp;•&nbsp;

            Farm ERP & IoT Intelligence

            &nbsp;•&nbsp;

            {currentDate}

        </div>


        {loading && (<div className="dashboard-loading">

            Synchronizing farm intelligence...

        </div>)}

    </div>);
}
/* ================================================================
   SECTION TITLE
   ================================================================ */
function SectionTitle({
                          title, className = ""
                      }) {
    return (<div
        className={`dashboard-section-title ${className}`}
    >

        <div
            className="section-title-marker"
        />

        <span>
                {title}
            </span>

    </div>);
}
/* ================================================================
   ENTERPRISE CARD
   ================================================================ */
function EnterpriseCard({
                            title, subtitle, children
                        }) {
    return (<div className="enterprise-card">

        <div className="enterprise-card-header">

            <div>

                <div className="enterprise-card-title">

                    {title}

                </div>


                {subtitle && (<div
                    className="enterprise-card-subtitle"
                >

                    {subtitle}

                </div>)}

            </div>

        </div>


        <div className="enterprise-card-body">

            {children}

        </div>

    </div>);
}
/* ================================================================
   KPI CARD
   ================================================================ */
function DashboardCard({
                           title, value, subtitle, icon, accent
                       }) {
    return (<div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">

        <div
            className={`dashboard-kpi-card kpi-${accent}`}
        >

            <div className="kpi-accent"/>


            <div className="kpi-content">

                <div className="kpi-title">

                    {title}

                </div>


                <div className="kpi-value">

                    {value}

                </div>


                <div className="kpi-subtitle">

                    {subtitle}

                </div>

            </div>


            <div className="kpi-icon">

                {icon}

            </div>

        </div>

    </div>);
}
/* ================================================================
   SENSOR CARD
   ================================================================ */
function SensorCard({
    title,
    value,
    unit,
    subtitle,
    icon,
    accent = "green"
}) {
    return (
        <div className="col-xl-2 col-lg-4 col-md-6 col-sm-6">

            <div className={`agri-sensor-card agri-sensor-${accent}`}>

                <div className="agri-sensor-card-top">

                    <div className="agri-sensor-icon">
                        {icon}
                    </div>

                    <div className="agri-sensor-live">
                        <span></span>
                        LIVE
                    </div>

                </div>


                <div className="agri-sensor-title">
                    {title}
                </div>


                <div className="agri-sensor-reading">

                    <span className="agri-sensor-value">
                        {value}
                    </span>

                    {unit && (
                        <span className="agri-sensor-unit">
                            {unit}
                        </span>
                    )}

                </div>


                <div className="agri-sensor-subtitle">
                    {subtitle}
                </div>


                <div className="agri-sensor-bottom-line"></div>

            </div>

        </div>
    );
}

/* ================================================================
   CONTROL CARD
   ================================================================ */
function ControlCard({
                         icon, title, value, subtitle, danger
                     }) {
    return (<div
        className={danger ? "agri-control-card danger" : "agri-control-card"}
    >

        <div className="control-icon">

            {icon}

        </div>


        <div>

            <div className="control-title">

                {title}

            </div>


            <div className="control-value">

                {value}

            </div>


            <div className="control-subtitle">

                {subtitle}

            </div>

        </div>

    </div>);
}
/* ================================================================
   EMPTY STATE
   ================================================================ */
function EmptyState({
                        text
                    }) {
    return (<div className="empty-state">

        {text}

    </div>);
}
export default Home;
